// What is typed in the prompt's box, drawn a second time behind its letters,
// which are transparent: backtick code is marked as it is typed. Every
// character of the draft is here, in its place and in the box's own font,
// and nothing else, so each letter falls on the one the box holds; the box
// keeps the caret, the selection and the keyboard. Moved from today's Code
// tab (code/PromptInk.tsx there), without its colouring of a block's code:
// the new pages have no highlighter yet, so a block is drawn as its text.
import { useMemo, type ReactNode, type Ref } from 'react'
import { promptParts } from '../transcript/promptCode'

/** The parts of the draft, drawn: backticks and fence lines faint, code tinted. */
function ink(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  // A block takes the line break after its closing fence: a line that starts after a block starts with no break of its own.
  let at = 0
  let open = false
  for (const part of promptParts(text)) {
    const start = Math.max(part.start, at)
    if (part.kind === 'text') {
      if (start < part.end) nodes.push(text.slice(start, part.end))
    } else if (part.kind === 'inline') {
      nodes.push(
        <span key={part.start} className="code-ink-inline">
          <span className="code-ink-mark">`</span>
          {text.slice(part.from, part.to)}
          <span className="code-ink-mark">`</span>
        </span>,
      )
    } else {
      const head = text.slice(part.start, part.from)
      const labelAt = part.label ? head.indexOf(part.label, 3) : -1
      const end = part.end + (/^\r?\n/.exec(text.slice(part.end, part.end + 2))?.[0].length ?? 0)
      nodes.push(
        <span key={part.start} className="code-ink-block">
          {labelAt < 0 ? (
            <span className="code-ink-mark">{head}</span>
          ) : (
            <>
              <span className="code-ink-mark">{head.slice(0, labelAt)}</span>
              <span className="code-ink-lang">{part.label}</span>
              <span className="code-ink-mark">{head.slice(labelAt + part.label.length)}</span>
            </>
          )}
          {text.slice(part.from, part.to)}
          {part.closed ? <span className="code-ink-mark">{text.slice(part.to, end)}</span> : text.endsWith('\n') && ' '}
        </span>,
      )
      open = !part.closed
      at = end
      continue
    }
    at = part.end
  }
  // A line break at the very end draws no line of its own, where the box has the caret's.
  // A block with no closing fence runs to the end, and holds that line itself.
  if (text.endsWith('\n') && !open) nodes.push(' ')
  return nodes
}

export function PromptInk({ text, ref }: { text: string; ref?: Ref<HTMLDivElement> }) {
  // The box shows its value with a line feed for each CR LF and lone CR: so does the ink.
  const nodes = useMemo(() => ink(text.replace(/\r\n?/g, '\n')), [text])
  return (
    <div ref={ref} className="markdown-body code-prompt-ink" aria-hidden="true">
      {nodes}
    </div>
  )
}
